import { useState, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, FileText, Image, X, FlaskConical, Loader2 } from 'lucide-react';
import { useUploadBloodwork } from '@/api/hooks/useBloodwork';
import { useCurrentProtocol } from '@/api/hooks/useProtocol';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';

const ACCEPTED_TYPES = ['application/pdf', 'image/png', 'image/jpeg'];

const toBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const PROGRESS_STAGES = [
  'Uploading...',
  'Extracting biomarkers...',
  'Analyzing results...',
  'Generating recommendations...',
];

export function UploadPage() {
  const [file, setFile] = useState<File | null>(null);
  const [labDate, setLabDate] = useState('');
  const [labProvider, setLabProvider] = useState('');
  const [fasting, setFasting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [progressStage, setProgressStage] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const upload = useUploadBloodwork();
  const { data: protocolData } = useCurrentProtocol();
  const navigate = useNavigate();

  const validateFile = useCallback((f: File): boolean => {
    if (!ACCEPTED_TYPES.includes(f.type)) {
      return false;
    }
    if (f.size > 20 * 1024 * 1024) {
      return false;
    }
    return true;
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragActive(false);
      const dropped = e.dataTransfer.files[0];
      if (dropped && validateFile(dropped)) {
        setFile(dropped);
      }
    },
    [validateFile]
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selected = e.target.files?.[0];
      if (selected && validateFile(selected)) {
        setFile(selected);
      }
    },
    [validateFile]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !labDate) return;

    setProgressStage(0);
    const interval = setInterval(() => {
      setProgressStage((s) => Math.min(s + 1, PROGRESS_STAGES.length - 1));
    }, 2000);

    try {
      const fileData = await toBase64(file);
      const fileTypeValue: 'pdf' | 'image' = file.type === 'application/pdf' ? 'pdf' : 'image';
      const result = await upload.mutateAsync({
        file_data: fileData,
        file_type: fileTypeValue,
        lab_date: labDate,
        lab_provider: labProvider || 'other',
        fasting,
      });
      clearInterval(interval);
      navigate(`/results/${result.uploadId}`);
    } catch {
      clearInterval(interval);
    }
  };

  const canSubmit = file && labDate && !upload.isPending;

  const fileIcon =
    file?.type === 'application/pdf' ? (
      <FileText className="h-5 w-5 text-red-500" />
    ) : (
      <Image className="h-5 w-5 text-blue-500" />
    );

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-navy">Upload Lab Results</h1>
        <p className="mt-1 text-gray-500">
          Upload your bloodwork PDF or image for analysis
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Left: Upload area */}
          <Card>
            <CardHeader>
              <CardTitle>Lab Document</CardTitle>
            </CardHeader>
            <CardContent>
              <AnimatePresence mode="wait">
                {!file ? (
                  <motion.div
                    key="dropzone"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <div
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDragActive(true);
                      }}
                      onDragLeave={() => setDragActive(false)}
                      onDrop={handleDrop}
                      onClick={() => fileInputRef.current?.click()}
                      className={`flex flex-col items-center justify-center rounded-xl border-2 border-dashed p-12 cursor-pointer transition-colors ${
                        dragActive
                          ? 'border-copper bg-copper/5'
                          : 'border-gray-300 hover:border-copper/50 hover:bg-gray-50'
                      }`}
                    >
                      <Upload
                        className={`h-10 w-10 mb-4 ${
                          dragActive ? 'text-copper' : 'text-gray-400'
                        }`}
                      />
                      <p className="text-sm font-medium text-gray-700 mb-1">
                        Drop your lab PDF or image here
                      </p>
                      <p className="text-xs text-gray-400">
                        or click to browse. PDF, PNG, JPG accepted (max 20 MB)
                      </p>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </motion.div>
                ) : (
                  <motion.div
                    key="preview"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="flex items-center gap-4 rounded-xl border border-gray-200 bg-gray-50 p-4"
                  >
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-white border border-gray-200">
                      {fileIcon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-navy truncate">
                        {file.name}
                      </p>
                      <p className="text-xs text-gray-400">
                        {formatFileSize(file.size)}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setFile(null)}
                      className="p-1.5 rounded-lg hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </CardContent>
          </Card>

          {/* Right: Intake form */}
          <Card>
            <CardHeader>
              <CardTitle>Lab Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="lab-date">Lab Date *</Label>
                <Input
                  id="lab-date"
                  type="date"
                  value={labDate}
                  onChange={(e) => setLabDate(e.target.value)}
                  required
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="lab-provider">Lab Provider</Label>
                <Select value={labProvider} onValueChange={setLabProvider}>
                  <SelectTrigger id="lab-provider">
                    <SelectValue placeholder="Select provider" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="labcorp">LabCorp</SelectItem>
                    <SelectItem value="quest">Quest Diagnostics</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="fasting">Fasting sample?</Label>
                <Switch
                  id="fasting"
                  checked={fasting}
                  onCheckedChange={setFasting}
                />
              </div>

              {/* Current protocol display */}
              <div className="rounded-lg bg-gray-50 border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <FlaskConical className="h-4 w-4" />
                    Current Protocol
                  </div>
                  <Link
                    to="/protocol"
                    className="text-xs text-copper hover:text-copper-dark font-medium"
                  >
                    Edit
                  </Link>
                </div>
                {protocolData?.protocol ? (
                  <div>
                    <Badge variant="secondary" className="capitalize mb-2">
                      {protocolData.protocol.status}
                    </Badge>
                    {protocolData.protocol.items?.length ? (
                      <ul className="text-xs text-gray-500 space-y-1">
                        {protocolData.protocol.items.slice(0, 3).map((item, i) => (
                          <li key={i}>
                            {item.name}
                            {item.dosage ? ` - ${item.dosage}` : ''}
                            {item.frequency ? ` (${item.frequency})` : ''}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400">
                    No protocol set.{' '}
                    <Link
                      to="/protocol"
                      className="text-copper hover:text-copper-dark"
                    >
                      Add one
                    </Link>
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Error */}
        {upload.error && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mb-6 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700"
          >
            {upload.error.message}
          </motion.div>
        )}

        {/* Submit */}
        <Button
          type="submit"
          size="lg"
          className="w-full h-12 text-base"
          disabled={!canSubmit}
        >
          {upload.isPending ? (
            <span className="flex items-center gap-3">
              <Loader2 className="h-5 w-5 animate-spin" />
              {PROGRESS_STAGES[progressStage]}
            </span>
          ) : (
            'Analyze My Labs'
          )}
        </Button>
      </form>
    </div>
  );
}
